import { useEffect, useState } from 'react';
import { api } from './api.js';

export interface PanelBranding {
  name: string;
  tagline: string;
  logo_url: string;
  /** Hex string like '#34d399', or '' for the built-in emerald. */
  accent_color: string;
}

export const DEFAULT_ACCENT = '#34d399';

const DEFAULT_BRANDING: PanelBranding = {
  name: 'Clean Panel',
  tagline: 'Rust server control',
  logo_url: '',
  accent_color: '',
};

let cached: PanelBranding | null = null;
const subs = new Set<(b: PanelBranding) => void>();

function notify(b: PanelBranding) {
  cached = b;
  document.title = b.name;
  applyFavicon(b.logo_url);
  applyAccentColor(b.accent_color || DEFAULT_ACCENT);
  for (const s of subs) s(b);
}

/** Public branding fetch — works pre-login (used by Login page). */
export async function loadPublicBranding(): Promise<PanelBranding> {
  try {
    const b = await api.get<Partial<PanelBranding>>('/api/panel/branding');
    notify({ ...DEFAULT_BRANDING, ...b });
    return cached!;
  } catch {
    notify(DEFAULT_BRANDING);
    return DEFAULT_BRANDING;
  }
}

/** Authenticated config (admin-editable) — same shape as branding plus extras. */
export async function loadAuthedConfig(): Promise<PanelBranding & { discord_guild_id: string }> {
  const cfg = await api.get<PanelBranding & { discord_guild_id: string }>('/api/panel/config');
  notify({
    name: cfg.name,
    tagline: cfg.tagline,
    logo_url: cfg.logo_url,
    accent_color: cfg.accent_color ?? '',
  });
  return cfg;
}

/** Reactive hook used by Layout/Login to pull the latest branding. */
export function useBranding(): PanelBranding {
  const [b, setB] = useState<PanelBranding>(cached ?? DEFAULT_BRANDING);
  useEffect(() => {
    subs.add(setB);
    return () => { subs.delete(setB); };
  }, []);
  return b;
}

/** Helper for the Settings tab to push a new value optimistically. Spreads
 *  over the current cached config so partial updates (e.g. logo upload) don't
 *  drop unrelated fields like accent_color. */
export function setBranding(next: Partial<PanelBranding>) {
  notify({ ...(cached ?? DEFAULT_BRANDING), ...next });
}

/** Live-preview helper used by the picker before the user clicks Save. Only
 *  touches CSS vars — doesn't mutate the cached config or fire subscribers. */
export function previewAccentColor(hex: string) {
  applyAccentColor(hex || DEFAULT_ACCENT);
}

/** Swap the document's <link rel="icon"> to track the configured logo. Falls
 *  back to the bundled SVG when no custom logo is set. */
function applyFavicon(url: string): void {
  const target = url || '/favicon.svg';
  const links = Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]'));
  if (links.length === 0) {
    const link = document.createElement('link');
    link.rel = 'icon';
    link.href = target;
    document.head.appendChild(link);
    return;
  }
  for (const link of links) {
    link.href = target;
    link.removeAttribute('type');
  }
}

/** Push an accent color into the page-level CSS variables. Computes a hover
 *  shade (lerp 8% toward white) and a dim shade (lerp 55% toward black) so
 *  hover states and muted backgrounds stay coherent with the picked color. */
function applyAccentColor(hex: string): void {
  const rgb = hexToRgb(hex) ?? hexToRgb(DEFAULT_ACCENT)!;
  const hover = lerp(rgb, { r: 255, g: 255, b: 255 }, 0.08);
  const dim   = lerp(rgb, { r: 0,   g: 0,   b: 0   }, 0.55);
  const root = document.documentElement.style;
  root.setProperty('--accent-rgb',       `${rgb.r} ${rgb.g} ${rgb.b}`);
  root.setProperty('--accent-hover-rgb', `${hover.r} ${hover.g} ${hover.b}`);
  root.setProperty('--accent-dim-rgb',   `${dim.r} ${dim.g} ${dim.b}`);
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return { r: (n >> 16) & 0xff, g: (n >> 8) & 0xff, b: n & 0xff };
}

function lerp(a: { r: number; g: number; b: number }, b: { r: number; g: number; b: number }, t: number) {
  return {
    r: Math.round(a.r + (b.r - a.r) * t),
    g: Math.round(a.g + (b.g - a.g) * t),
    b: Math.round(a.b + (b.b - a.b) * t),
  };
}
